import { inject, Injectable } from '@angular/core';
import { IEstimateResultModel, LastEstimate } from '../models/estimate-result-bundle.model';
import { HttpResponse } from '@angular/common/http';
import { Transport } from '../models/transport.model';
import { map, switchMap } from 'rxjs/operators';
import { forkJoin, Observable } from 'rxjs';
import { API_ENDPOINTS } from '../models/enums/endpoints.enum';
import { ApiProviderService } from './api-provider.service';
import { BaryErrorService } from './bary-error-service';
import { BaryInseeService } from './bary-insee.service';
import { OfferEstimation } from '../models/offer-estimation.model';
import { IBaryCommune } from '../models/geo-api-commune.model';
import { mapAsBody } from '../app/shared/util/request-util';

@Injectable({ providedIn: 'root' })
export class EstimateService {
  readonly #apiProvider = inject(ApiProviderService);
  readonly #baryErrorService = inject(BaryErrorService);
  readonly #baryInseeService = inject(BaryInseeService);

  public estimateUrl: string = API_ENDPOINTS.ESTIMATE;

  public estimatePricing(transport: Transport): Observable<HttpResponse<OfferEstimation[]>> {
    return this.checkInseeCode(transport).pipe(
      switchMap(data => {
        transport.pickupAddressInseeCode = data.dataPickup?.code;
        transport.pickupAddressGpsCoords = data.dataPickup?.centre?.coordinates;
        transport.deliveryAddressInseeCode = data.dataDelivery?.code;
        transport.deliveryAddressGpsCoords = data.dataDelivery?.centre?.coordinates;
        return this.#apiProvider.publicPost<OfferEstimation[]>(this.estimateUrl, transport.serialize());
      })
    );
  }

  public checkInseeCode(transport: Transport): Observable<{
    dataPickup?: IBaryCommune;
    dataDelivery?: IBaryCommune;
  }> {
    return forkJoin({
      dataPickup: this.#baryInseeService.getInseeAndCoordinatesByCP(
        transport.pickupAddressZipCode,
        transport.pickupAddressCity,
        transport.pickupAddressInseeCode,
        transport.pickupAddressGpsCoords
      ),
      dataDelivery: this.#baryInseeService.getInseeAndCoordinatesByCP(
        transport.deliveryAddressZipCode,
        transport.deliveryAddressCity,
        transport.deliveryAddressInseeCode,
        transport.deliveryAddressGpsCoords
      ),
    });
  }

  public refreshPricing(transport: Transport, customerId?: number): Observable<LastEstimate> {
    return this.checkInseeCode(transport).pipe(
      switchMap(data => {
        transport.pickupAddressInseeCode = data.dataPickup?.code;
        transport.pickupAddressGpsCoords = data.dataPickup?.centre?.coordinates;
        transport.deliveryAddressInseeCode = data.dataDelivery?.code;
        transport.deliveryAddressGpsCoords = data.dataDelivery?.centre?.coordinates;
        return this.#apiProvider
          .publicPost<IEstimateResultModel>(`${this.estimateUrl}/pricing${customerId ? `?customerId=${customerId}` : ''}`, transport.serialize())
          .pipe(mapAsBody)
          .pipe(map(res => this.mapAsLastEstimate(res)));
      })
    );
  }

  public refreshPricingForUpdate(transport: Transport, transportOrderId: number, customerId: number | undefined): Observable<LastEstimate> {
    return this.checkInseeCode(transport).pipe(
      switchMap(data => {
        transport.pickupAddressInseeCode = data.dataPickup?.code;
        transport.pickupAddressGpsCoords = data.dataPickup?.centre?.coordinates;
        transport.deliveryAddressInseeCode = data.dataDelivery?.code;
        transport.deliveryAddressGpsCoords = data.dataDelivery?.centre?.coordinates;
        return this.#apiProvider
          .post<IEstimateResultModel>(this.estimateUrl + '/pricing/update?transportOrderId=' + transportOrderId + '&customerId=' + customerId, transport.serialize())
          .pipe(mapAsBody)
          .pipe(map(res => this.mapAsLastEstimate(res)));
      })
    );
  }

  public validateStep(transport: Transport): Observable<LastEstimate> {
    return this.checkInseeCode(transport).pipe(
      switchMap(data => {
        transport.pickupAddressInseeCode = data.dataPickup?.code;
        transport.deliveryAddressInseeCode = data.dataDelivery?.code;
        return this.#apiProvider
          .publicPost<IEstimateResultModel>(this.estimateUrl + '/validate', transport.serialize())
          .pipe(mapAsBody)
          .pipe(map(res => this.mapAsLastEstimate(res)));
      })
    );
  }

  private mapAsLastEstimate(res: IEstimateResultModel): LastEstimate {
    const lastEstimate = new LastEstimate(res);
    lastEstimate.errorMessages = this.#baryErrorService.baryErrorsToString(res!.errorMessages);
    lastEstimate.quotationMessages = this.#baryErrorService.baryErrorsToString(res!.quotationMessages);
    return lastEstimate;
  }
}
